/**
 * EmargementMobile.jsx
 * Page mobile d'émargement — accessible via token, sans authentification
 * Deux flux : présence (AM/PM) et remise de certificat
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTraceSignature } from '../components/Shared';

// L'API est joignable via la même origine que le frontend (nginx proxy)
const API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

// ─── Canvas de signature ───────────────────────────────────────────────────────
function SignatureCanvas({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const trace = useTraceSignature(canvasRef, { largeurTrait: 2.5 });

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  const save = () => {
    const canvas = canvasRef.current;
    // Vérifier que la signature n'est pas vide
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasContent = data.some((v, i) => i % 4 === 3 && v > 0);
    if (!hasContent) { alert('Veuillez signer avant de valider.'); return; }
    onSave(canvas.toDataURL('image/png'));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    // Adapter la résolution au DPR
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13, color: '#555', textAlign: 'center' }}>
        Tracez votre signature ci-dessous
      </div>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%', height: 140, border: '2px solid #1a3668', borderRadius: 8,
          background: '#f8faff', touchAction: 'none', cursor: 'crosshair',
        }}
        {...trace}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={clear} style={btnStyle('#6b7280')}>Effacer</button>
        <button onClick={onCancel} style={btnStyle('#9ca3af')}>Annuler</button>
        <button onClick={save} style={{ ...btnStyle('#1a3668'), flex: 2 }}>✓ Valider la signature</button>
      </div>
    </div>
  );
}

// ─── Fiche d'un stagiaire ──────────────────────────────────────────────────────
function FicheStagiaire({ stagiaire, signature, typeEmargement, onSigner, onReset, amenagement }) {
  const [expanded, setExpanded] = useState(false);
  const [statut, setStatut] = useState('present');
  const [motif, setMotif] = useState('');
  const [showCanvas, setShowCanvas] = useState(false);
  const [loading, setLoading] = useState(false);

  const estSigne = !!signature;
  const estAbsent = signature?.statut_presence === 'absent';

  const handleValider = async (signatureBase64) => {
    setLoading(true);
    try {
      await onSigner({
        stagiaire_id: stagiaire.stagiaire_id,
        nom_signataire: [stagiaire.civilite, stagiaire.prenoms, stagiaire.nom].filter(Boolean).join(' ').trim() || stagiaire.nom_complet || '',
        statut_presence: statut,
        motif_absence: statut === 'absent' ? motif : null,
        signature_base64: signatureBase64,
      });
      setShowCanvas(false);
      setExpanded(false);
    } finally { setLoading(false); }
  };

  const handleAbsent = async () => {
    if (!motif.trim() && typeEmargement === 'presence') {
      alert('Veuillez saisir un motif d\'absence.');
      return;
    }
    setLoading(true);
    try {
      await onSigner({
        stagiaire_id: stagiaire.stagiaire_id,
        nom_signataire: [stagiaire.civilite, stagiaire.prenoms, stagiaire.nom].filter(Boolean).join(' ').trim() || stagiaire.nom_complet || '',
        statut_presence: 'absent',
        motif_absence: motif || null,
        signature_base64: 'ABSENT',
      });
      setExpanded(false);
    } finally { setLoading(false); }
  };

  const statutColor = estSigne
    ? (estAbsent ? '#f97316' : '#16a34a')
    : '#6b7280';

  const statutLabel = estSigne
    ? (estAbsent ? '✗ Absent' : '✓ Signé')
    : '— En attente';

  return (
    <div style={{
      border: `2px solid ${estSigne ? (estAbsent ? '#fed7aa' : '#bbf7d0') : '#e5e7eb'}`,
      borderRadius: 10,
      overflow: 'hidden',
      background: estSigne ? (estAbsent ? '#fff7ed' : '#f0fdf4') : '#fff',
    }}>
      {/* En-tête stagiaire */}
      <div
        onClick={() => !estSigne && setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', cursor: estSigne ? 'default' : 'pointer',
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1f2937' }}>{[stagiaire.civilite, stagiaire.prenoms, stagiaire.nom].filter(Boolean).join(' ') || stagiaire.nom_complet || ''}</div>
          {estAbsent && signature?.motif_absence && (
            <div style={{ fontSize: 12, color: '#f97316', marginTop: 2 }}>Motif : {signature.motif_absence}</div>
          )}
          {amenagement && (
            <div style={{ marginTop: 4, background: 'rgba(124,58,237,0.1)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: '#7c3aed' }}>🕐 Horaires aménagés : {amenagement.nouveaux_horaires}</span>
              {amenagement.motif && <div style={{ color: '#6b21a8', marginTop: 2, fontSize: 11, fontStyle: 'italic' }}>{amenagement.motif}</div>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: statutColor }}>
            {statutLabel}
            {!estSigne && <span style={{ color: '#9ca3af', fontSize: 18, marginLeft: 4 }}>{expanded ? '▲' : '▼'}</span>}
          </span>
          {estSigne && onReset && (
            <button
              onClick={e => {
                e.stopPropagation();
                const nom = [stagiaire.civilite, stagiaire.prenoms, stagiaire.nom].filter(Boolean).join(' ') || stagiaire.nom_complet || 'ce stagiaire';
                onReset(stagiaire.stagiaire_id, nom);
              }}
              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #dc2626', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600, lineHeight: 1.4 }}
              title="Annuler la signature"
            >✕ Annuler</button>
          )}
        </div>
      </div>

      {/* Panel signature (si non signé et ouvert) */}
      {!estSigne && expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid #e5e7eb' }}>
          {typeEmargement === 'presence' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 10 }}>
              <button
                onClick={() => { setStatut('present'); setShowCanvas(false); }}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
                  background: statut === 'present' ? '#16a34a' : '#f3f4f6',
                  color: statut === 'present' ? '#fff' : '#374151',
                  fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
              >Présent</button>
              <button
                onClick={() => { setStatut('absent'); setShowCanvas(false); }}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
                  background: statut === 'absent' ? '#f97316' : '#f3f4f6',
                  color: statut === 'absent' ? '#fff' : '#374151',
                  fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
              >Absent</button>
            </div>
          )}

          {statut === 'absent' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                type="text"
                placeholder="Motif d'absence (ex: maladie, congé...)"
                value={motif}
                onChange={e => setMotif(e.target.value)}
                style={inputStyle}
              />
              <button onClick={handleAbsent} disabled={loading} style={btnStyle('#f97316')}>
                {loading ? '...' : 'Confirmer absence'}
              </button>
            </div>
          ) : (
            <>
              {!showCanvas ? (
                <button
                  onClick={() => setShowCanvas(true)}
                  style={{ ...btnStyle('#1a3668'), width: '100%', marginTop: 4 }}
                >
                  ✍ Signer
                </button>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <SignatureCanvas
                    onSave={handleValider}
                    onCancel={() => setShowCanvas(false)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Fiche formateur ──────────────────────────────────────────────────────────
function FicheFormateur({ nomFormateur, signature, onSigner }) {
  const [showCanvas, setShowCanvas] = useState(false);
  const [loading, setLoading] = useState(false);

  const estSigne = !!signature;

  const handleValider = async (signatureBase64) => {
    setLoading(true);
    try {
      await onSigner({
        stagiaire_id: null,
        nom_signataire: nomFormateur || 'Formateur',
        statut_presence: 'present',
        motif_absence: null,
        signature_base64: signatureBase64,
      });
      setShowCanvas(false);
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      border: `2px solid ${estSigne ? '#bfdbfe' : '#fbbf24'}`,
      borderRadius: 10, overflow: 'hidden',
      background: estSigne ? '#eff6ff' : '#fffbeb',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px',
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
            👨‍🏫 {nomFormateur || 'Formateur'}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Clôture de l'émargement</div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: estSigne ? '#2563eb' : '#d97706' }}>
          {estSigne ? '✓ Signé' : '⬜ En attente'}
        </div>
      </div>

      {!estSigne && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid #fde68a' }}>
          {!showCanvas ? (
            <button onClick={() => setShowCanvas(true)} style={{ ...btnStyle('#1a3668'), width: '100%', marginTop: 4 }}>
              ✍ Signer en tant que formateur
            </button>
          ) : (
            <div style={{ marginTop: 8 }}>
              <SignatureCanvas onSave={handleValider} onCancel={() => setShowCanvas(false)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Styles helpers ────────────────────────────────────────────────────────────
const btnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  flex: 1,
});
const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
};

// ─── Aménagement d'horaires : attestation d'un stagiaire ───────────────────────────────────────────────────────
// Signable seulement après l'accord de l'employeur (ou si aucun accord n'a pu être demandé : pas d'adresse e-mail d'entreprise)
function CarteAmenagement({ ligne, onSigner, clos }) {
  const [ouvert, setOuvert] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const bloque = !ligne.accord_ok;
  const ouvrable = !ligne.signe && !bloque && !clos;

  const valider = async (signatureBase64) => {
    setEnvoi(true);
    try {
      await onSigner({ ast_id: ligne.ast_id, nom_signataire: ligne.nom_stagiaire, signature_base64: signatureBase64 });
      setShowCanvas(false); setOuvert(false);
    } finally { setEnvoi(false); }
  };

  return (
    <div style={{
      border: `2px solid ${ligne.signe ? '#bbf7d0' : bloque ? '#e5e7eb' : '#ddd6fe'}`, borderRadius: 10, overflow: 'hidden',
      background: ligne.signe ? '#f0fdf4' : bloque ? '#f9fafb' : '#fff', opacity: bloque && !ligne.signe ? 0.85 : 1,
    }}>
      <div onClick={() => ouvrable && setOuvert(!ouvert)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px', cursor: ouvrable ? 'pointer' : 'default' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1f2937' }}>{ligne.nom_stagiaire}</div>
          {ligne.nom_client && <div style={{ fontSize: 12, color: '#6b7280' }}>{ligne.nom_client}</div>}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, textAlign: 'right', color: ligne.signe ? '#16a34a' : bloque ? '#92400e' : '#7c3aed' }}>
          {ligne.signe ? '✓ Signé' : bloque ? "⏳ En attente de l'accord de l'employeur" : <>À signer <span style={{ color: '#9ca3af', fontSize: 18 }}>{ouvert ? '▲' : '▼'}</span></>}
        </div>
      </div>

      {ouvrable && ouvert && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '12px 0' }}>
            <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Horaires initiaux</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>{ligne.horaires_originaux || '—'}</div>
            </div>
            <div style={{ background: '#dbeafe', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase' }}>Horaires aménagés</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8' }}>{ligne.nouveaux_horaires}</div>
            </div>
          </div>
          {ligne.motif && (
            <div style={{ background: '#fef9f0', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 3 }}>Motif de l'aménagement</div>
              <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.5 }}>{ligne.motif}</div>
            </div>
          )}
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: '0 0 10px' }}>
            En signant cette attestation, vous confirmez avoir pris connaissance de cet aménagement des horaires de formation et l'acceptez.
          </p>
          {!ligne.accord_requis && (
            <p style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic', margin: '0 0 10px' }}>Aucune adresse e-mail d'entreprise : l'accord de l'employeur n'a pas été demandé.</p>
          )}
          {!showCanvas ? (
            <button onClick={() => setShowCanvas(true)} disabled={envoi} style={{ ...btnStyle('#1a3668'), width: '100%' }}>✍ Signer</button>
          ) : (
            <SignatureCanvas onSave={valider} onCancel={() => setShowCanvas(false)} />
          )}
        </div>
      )}
    </div>
  );
}

function VueAmenagement({ data, sidParam, onSigner, successMsg }) {
  const { emargement, session, amenagement_lignes: lignes = [] } = data;
  const modeFormateurComplet = !sidParam;
  const affichees = sidParam ? lignes.filter(l => String(l.stagiaire_id) === String(sidParam)) : lignes;
  const nbSignes = lignes.filter(l => l.signe).length;
  const nbEnAttente = lignes.filter(l => !l.signe && !l.accord_ok).length;

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ background: 'linear-gradient(135deg, #5b21b6, #7c3aed)', color: '#fff', padding: '20px 20px 16px' }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>CEJ Concept — Formation</div>
        <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>🕐 Attestation d'aménagement des horaires</div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6 }}>{session?.intitule_formation}</div>
        {session?.lieu && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>📍 {session.lieu}</div>}
      </div>

      {modeFormateurComplet && (
        <div style={{ background: '#fff', padding: '10px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
            <span>{nbSignes} / {lignes.length} attestation(s) signée(s)</span>
            {nbEnAttente > 0 && <span style={{ color: '#92400e' }}>{nbEnAttente} en attente de l'employeur</span>}
          </div>
          <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 99, width: `${lignes.length ? (nbSignes / lignes.length) * 100 : 0}%`, background: 'linear-gradient(90deg, #5b21b6, #7c3aed)', transition: 'width .4s' }} />
          </div>
        </div>
      )}

      {successMsg && <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 20px', textAlign: 'center', fontSize: 14, fontWeight: 500 }}>{successMsg}</div>}
      {emargement.clos && <div style={{ background: '#dbeafe', color: '#1e40af', padding: '12px 20px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>🔒 Cette signature a été clôturée</div>}

      <div style={{ padding: '16px 16px 24px' }}>
        {modeFormateurComplet && <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>Stagiaires concernés ({lignes.length})</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {affichees.map(l => <CarteAmenagement key={l.ast_id} ligne={l} onSigner={onSigner} clos={emargement.clos} />)}
          {affichees.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 14 }}>
              {sidParam ? "Aucun aménagement d'horaires à signer pour vous." : "Aucun aménagement d'horaires à signer pour cette session."}
            </div>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'center', padding: '8px 16px 32px', fontSize: 11, color: '#9ca3af' }}>CEJ Concept — Système d'émargement dématérialisé</div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────
export default function EmargementMobile() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  // sid = stagiaire_id passé via QR code — mode vue unique du stagiaire
  const sidParam = searchParams.get('sid') || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${API}/emargement/${token}`);
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Erreur');
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  // Aménagement d'horaires : l'accord de l'employeur ou la signature d'un autre stagiaire peut arriver pendant que la page est ouverte
  const typeAmenagement = data?.emargement?.type === 'amenagement';
  useEffect(() => {
    if (!typeAmenagement) return undefined;
    const id = setInterval(charger, 10000);
    return () => clearInterval(id);
  }, [typeAmenagement, charger]);

  const resetSignature = async (stagiaireId, nomStagiaire) => {
    const motif = window.prompt(`Annuler la signature de ${nomStagiaire} ?\n\nMotif (optionnel) :`, '');
    if (motif === null) return; // annulé
    const r = await fetch(`${API}/emargement/${token}/signature/${stagiaireId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motif_annulation: motif || null }),
    });
    if (!r.ok) { const err = await r.json(); alert(err.error || 'Erreur'); return; }
    charger();
  };

  const signer = async (payload) => {
    const r = await fetch(`${API}/emargement/${token}/signer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const err = await r.json();
      alert(err.error || 'Erreur lors de la signature');
      return;
    }
    setSuccessMsg(`Signature de ${payload.nom_signataire} enregistrée ✓`);
    setTimeout(() => setSuccessMsg(null), 2500);
    charger(); // Recharger pour mettre à jour l'état
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
        Chargement…
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{
        textAlign: 'center', background: '#fff', borderRadius: 16,
        padding: 32, boxShadow: '0 4px 20px rgba(0,0,0,.1)', maxWidth: 340,
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: '#1f2937' }}>Lien non disponible</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>{error}</div>
      </div>
    </div>
  );

  if (typeAmenagement) return <VueAmenagement data={data} sidParam={sidParam} onSigner={signer} successMsg={successMsg} />;

  const { emargement, session, stagiaires, signatures, amenagements = {}, groupe } = data;
  const typeLabel = emargement.type === 'presence' ? 'Émargement de présence' : 'Remise du certificat de réalisation';
  const slotLabel = emargement.slot === 'AM' ? 'Matin' : emargement.slot === 'PM' ? 'Après-midi' : null;
  const dateStr = emargement.date
    ? new Date(emargement.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  // Mode stagiaire : n'afficher que la fiche du stagiaire qui a scanné son QR
  const modeFormateurComplet = !sidParam;
  const stagiairesAffiches = sidParam
    ? stagiaires.filter(s => String(s.stagiaire_id) === String(sidParam))
    : stagiaires;

  const nbSigne = stagiaires.filter(s => signatures[s.stagiaire_id]).length;
  const formateurSigne = !!signatures['__formateur__'];
  const tousSignes = nbSigne === stagiaires.length && formateurSigne;

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a3668, #2563eb)',
        color: '#fff', padding: '20px 20px 16px',
      }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
          CEJ Concept — Formation
        </div>
        <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>{typeLabel}</div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6 }}>
          {session?.intitule_formation}
        </div>
        {groupe && (
          <div style={{ fontSize: 12, marginTop: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 8px', display: 'inline-block' }}>
            👥 {groupe.nom}{groupe.nom_formateur_groupe ? ` — ${groupe.nom_formateur_groupe}` : ''}
          </div>
        )}
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
          {dateStr}{slotLabel ? ` — ${slotLabel}` : ''}
        </div>
        {session?.lieu && (
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>📍 {session.lieu}</div>
        )}
        {/* Bandeau aménagement global si au moins un stagiaire concerné */}
        {Object.keys(amenagements).length > 0 && (
          <div style={{ marginTop: 10, background: 'rgba(124,58,237,0.15)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>🕐 Aménagement d'horaires en vigueur</div>
            {Object.values(amenagements).slice(0, 1).map((a, i) => (
              <div key={i}>
                <span style={{ opacity: 0.9 }}>Nouveaux horaires : <strong>{a.nouveaux_horaires}</strong></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Barre de progression — uniquement en mode formateur */}
      {modeFormateurComplet && (
        <div style={{ background: '#fff', padding: '10px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
            <span>{nbSigne} / {stagiaires.length} stagiaires</span>
            <span>{formateurSigne ? '✓ Formateur signé' : '⬜ Formateur'}</span>
          </div>
          <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 99,
              width: `${stagiaires.length ? (nbSigne / stagiaires.length) * 100 : 0}%`,
              background: 'linear-gradient(90deg, #1a3668, #2563eb)',
              transition: 'width .4s',
            }} />
          </div>
        </div>
      )}

      {/* Message succès */}
      {successMsg && (
        <div style={{
          background: '#d1fae5', color: '#065f46', padding: '10px 20px',
          textAlign: 'center', fontSize: 14, fontWeight: 500,
        }}>
          {successMsg}
        </div>
      )}

      {/* Message clôturé */}
      {emargement.clos && (
        <div style={{
          background: '#dbeafe', color: '#1e40af', padding: '12px 20px',
          textAlign: 'center', fontSize: 14, fontWeight: 600,
        }}>
          🔒 Cet émargement a été clôturé
        </div>
      )}

      {/* Liste stagiaires */}
      <div style={{ padding: '16px 16px 8px' }}>
        {modeFormateurComplet && (
          <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>
            Stagiaires ({stagiaires.length})
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stagiairesAffiches.map(s => (
            <FicheStagiaire
              key={s.stagiaire_id}
              stagiaire={s}
              signature={signatures[s.stagiaire_id]}
              typeEmargement={emargement.type}
              onSigner={signer}
              onReset={modeFormateurComplet && !emargement.clos ? resetSignature : undefined}
              amenagement={amenagements[String(s.stagiaire_id)] || null}
            />
          ))}
          {sidParam && stagiairesAffiches.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 14 }}>
              Stagiaire non trouvé dans cet émargement.
            </div>
          )}
        </div>
      </div>

      {/* Section formateur — visible uniquement en mode formateur (accès direct, sans ?sid=) */}
      {modeFormateurComplet && (
        <div style={{ padding: '8px 16px 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>
            Formateur — Clôture
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
            {nbSigne} / {stagiaires.length} stagiaire(s) ont signé.
          </div>
          <FicheFormateur
            nomFormateur={session?.nom_formateur}
            signature={signatures['__formateur__']}
            onSigner={signer}
          />
        </div>
      )}

      {/* Récap final */}
      {tousSignes && !emargement.clos && (
        <div style={{
          margin: '0 16px 32px', background: '#d1fae5', border: '2px solid #6ee7b7',
          borderRadius: 12, padding: 16, textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>🎉</div>
          <div style={{ fontWeight: 700, color: '#065f46', fontSize: 15 }}>
            Émargement complet !
          </div>
          <div style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>
            Tous les participants ont signé. Le formateur peut clôturer depuis l'application.
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '8px 16px 32px', fontSize: 11, color: '#9ca3af' }}>
        CEJ Concept — Système d'émargement dématérialisé
      </div>
    </div>
  );
}
